/**
 * Portal host resolver for FundElite + FundGenius official and custom domains.
 * Wraps hostnamePortal with a stable agency/custom-domain ready API.
 */

import {
  getHostnamePortal,
  HOSTNAME_PORTALS,
  isFundGeniusAppHostname,
  isFundGeniusIntakeHostname,
  isFundGeniusMarketingHostname,
} from './hostnamePortal';
import {
  APP_DOMAIN,
  INTAKE_DOMAIN,
  PUBLIC_DOMAIN,
} from '../config/fundgeniusDomains';

export const PORTAL_HOST_KINDS = {
  FUNDELITE_APP: 'fundelite_app',
  FUNDELITE_PLATFORM: 'fundelite_platform',
  FUNDGENIUS_PUBLIC: 'fundgenius_public',
  FUNDGENIUS_APP: 'fundgenius_app',
  FUNDGENIUS_INTAKE: 'fundgenius_intake',
  AGENCY_CUSTOM: 'agency_custom_domain',
  DEV: 'dev',
  UNKNOWN: 'unknown',
};

function normalizeHostname(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  return String(hostname || '').toLowerCase().split(':')[0].replace(/^www\./, (m, _o, s) => {
    // keep www. for marketing discrimination via helpers, but also accept bare
    return m;
  });
}

export function resolvePortalHost(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  const portal = getHostnamePortal(host);

  if (portal === HOSTNAME_PORTALS.DEV) {
    return {
      kind: PORTAL_HOST_KINDS.DEV,
      hostname: host,
      portalType: 'dev',
      agencyId: null,
      agencyType: null,
    };
  }

  if (host === PUBLIC_DOMAIN || host === `www.${PUBLIC_DOMAIN}` || isFundGeniusMarketingHostname(host)) {
    return {
      kind: PORTAL_HOST_KINDS.FUNDGENIUS_PUBLIC,
      hostname: host,
      portalType: 'public',
      agencyId: null,
      agencyType: 'fundgenius',
    };
  }

  if (host === APP_DOMAIN || isFundGeniusAppHostname(host)) {
    return {
      kind: PORTAL_HOST_KINDS.FUNDGENIUS_APP,
      hostname: host,
      portalType: 'app',
      agencyId: null,
      agencyType: 'fundgenius',
    };
  }

  if (host === INTAKE_DOMAIN || isFundGeniusIntakeHostname(host)) {
    return {
      kind: PORTAL_HOST_KINDS.FUNDGENIUS_INTAKE,
      hostname: host,
      portalType: 'intake',
      agencyId: null,
      agencyType: 'fundgenius',
    };
  }

  if (portal === HOSTNAME_PORTALS.ADMIN) {
    return {
      kind: PORTAL_HOST_KINDS.FUNDELITE_PLATFORM,
      hostname: host,
      portalType: 'platform',
      agencyId: null,
      agencyType: 'credit_repair',
    };
  }

  if (portal === HOSTNAME_PORTALS.CUSTOMER || portal === HOSTNAME_PORTALS.MARKETING) {
    return {
      kind: PORTAL_HOST_KINDS.FUNDELITE_APP,
      hostname: host,
      portalType: portal === HOSTNAME_PORTALS.MARKETING ? 'public' : 'app',
      agencyId: null,
      agencyType: 'credit_repair',
    };
  }

  // Unknown production host — treat as potential agency custom domain.
  if (host && !host.includes('localhost') && !host.includes('127.0.0.1')) {
    return {
      kind: PORTAL_HOST_KINDS.AGENCY_CUSTOM,
      hostname: host,
      portalType: 'custom',
      agencyId: null,
      agencyType: null,
      needsDomainLookup: true,
    };
  }

  return {
    kind: PORTAL_HOST_KINDS.UNKNOWN,
    hostname: host,
    portalType: 'unknown',
    agencyId: null,
    agencyType: null,
  };
}

export function isFundGeniusPublicHost(hostname) {
  return resolvePortalHost(hostname).kind === PORTAL_HOST_KINDS.FUNDGENIUS_PUBLIC;
}

export function isFundGeniusAppHost(hostname) {
  return resolvePortalHost(hostname).kind === PORTAL_HOST_KINDS.FUNDGENIUS_APP;
}

export function isFundGeniusIntakeHost(hostname) {
  return resolvePortalHost(hostname).kind === PORTAL_HOST_KINDS.FUNDGENIUS_INTAKE;
}

export default {
  PORTAL_HOST_KINDS,
  resolvePortalHost,
  isFundGeniusPublicHost,
  isFundGeniusAppHost,
  isFundGeniusIntakeHost,
};
